export function formatLocaleNumber(value: number = 0, localeCode?: string, option?: Record<string, any>) {
    try {
        // if (!value) return '-';

        localeCode = localeCode || navigator.language || 'ko-KR';
        option = option || {};

        return new Intl.NumberFormat(localeCode, option).format(value);
    } catch (e) {
        return '-';
    }
}

export function formatLocaleTime(dateString: string, localeCode?: string, option?: Record<string, any>) {
    try {
        // 현재 i18n 언어 설정 가져오기
        let currentLanguage = 'ko';
        try {
            // localStorage에서 현재 언어 확인
            const storedLanguage = localStorage.getItem('language-storage');
            if (storedLanguage) {
                const parsed = JSON.parse(storedLanguage);
                const lang = parsed.state?.currentLanguage || parsed.currentLanguage;
                if (lang) currentLanguage = lang;
            } else {
                // i18next localStorage 확인
                const i18nextLng = localStorage.getItem('i18nextLng');
                if (i18nextLng) currentLanguage = i18nextLng;
            }
        } catch (e) {
            console.warn('Failed to get current language:', e);
        }

        // 언어 코드를 적절한 locale로 매핑
        const localeMapping: Record<string, string> = {
            ko: 'ko-KR',
            en: 'en-US',
            ja: 'ja-JP',
            'zh-CN': 'zh-CN',
            'zh-TW': 'zh-TW',
            vi: 'vi-VN',
        };

        localeCode = localeCode || localeMapping[currentLanguage] || 'ko-KR';
        option = option || {};

        const date = new Date(dateString);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return new Intl.DateTimeFormat(localeCode, {
            dateStyle: 'short',
            timeStyle: 'short',
            timeZone: timeZone,
            ...option,
        }).format(date);
    } catch (e) {
        console.error(e);
        return '-';
    }
}

export function formatLocaleDate(dateString: string, localeCode?: string, option?: Record<string, any>) {
    try {
        // 현재 i18n 언어 설정 가져오기
        let currentLanguage = 'ko';
        try {
            // localStorage에서 현재 언어 확인
            const storedLanguage = localStorage.getItem('language-storage');
            if (storedLanguage) {
                const parsed = JSON.parse(storedLanguage);
                const lang = parsed.state?.currentLanguage || parsed.currentLanguage;
                if (lang) currentLanguage = lang;
            } else {
                // i18next localStorage 확인
                const i18nextLng = localStorage.getItem('i18nextLng');
                if (i18nextLng) currentLanguage = i18nextLng;
            }
        } catch (e) {
            console.warn('Failed to get current language:', e);
        }

        // 언어 코드를 적절한 locale로 매핑
        const localeMapping: Record<string, string> = {
            ko: 'ko-KR',
            en: 'en-US',
            ja: 'ja-JP',
            'zh-CN': 'zh-CN',
            'zh-TW': 'zh-TW',
            vi: 'vi-VN',
        };

        localeCode = localeCode || localeMapping[currentLanguage] || 'ko-KR';
        option = option || {};

        const date = new Date(dateString);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return new Intl.DateTimeFormat(localeCode, {
            dateStyle: 'long',
            // timeStyle: 'short',
            timeZone: timeZone,
            ...option,
        }).format(date);
    } catch (e) {
        console.error(e);
        return '-';
    }
}

export function formatFilename(filename: string): string {
    // 현재 언어에 따른 앱 이름 매핑
    let currentLanguage = 'ko';
    try {
        const storedLanguage = localStorage.getItem('language-storage');
        if (storedLanguage) {
            const parsed = JSON.parse(storedLanguage);
            const lang = parsed.state?.currentLanguage || parsed.currentLanguage;
            if (lang) currentLanguage = lang;
        } else {
            const i18nextLng = localStorage.getItem('i18nextLng');
            if (i18nextLng) currentLanguage = i18nextLng;
        }
    } catch (e) {
        console.warn('Failed to get current language:', e);
    }

    const appNameMapping: Record<string, string> = {
        ko: '플로잉 상점관리자',
        en: 'Flowing Store Manager',
        ja: 'フローイング店舗管理者',
        'zh-CN': '流动商店管理员',
        'zh-TW': '流動商店管理員',
        vi: 'Quản lý cửa hàng Flowing',
    };

    const appName: string = appNameMapping[currentLanguage] || appNameMapping['ko'];
    const datetime = formatLocaleTime(new Date().toISOString()).replaceAll(':', '\:');
    const displayFilename = !!filename ? `-${filename}-` : '';
    console.log('formatFilename-datetime', datetime);

    return `${appName}${displayFilename}${datetime}`;
}

/**
 * 카테고리 이름을 i18n 키로 변환하는 함수
 * @param categoryName 카테고리 이름 (예: "샌들/슬리퍼")
 * @returns i18n 키 (예: "category.샌들_슬리퍼")
 */
export function getCategoryI18nKey(categoryName: string): string {
    if (!categoryName) return '';
    // 공백을 언더스코어로, 슬래시를 언더스코어로, 괄호 제거
    const key = categoryName.replace(/\s+/g, '_').replace(/\//g, '_').replace(/[()]/g, '');
    return `category.${key}`;
}

/**
 * 카테고리 이름을 번역하는 함수
 * @param categoryName 카테고리 이름 (예: "샌들/슬리퍼")
 * @param t i18n 번역 함수
 * @returns 번역된 카테고리 이름 또는 원본 이름 (번역이 없는 경우)
 */
export function translateCategoryName(categoryName: string | null | undefined, t: (key: string) => string): string {
    if (!categoryName) return '';
    const i18nKey = getCategoryI18nKey(categoryName);
    const translated = t(i18nKey);
    // 번역이 키와 같으면 (번역이 없으면) 원본 반환
    if (translated === i18nKey) {
        return categoryName;
    }
    return translated;
}

const PRODUCT_DETAIL_REFERENCE_VALUE = '상품상세참조';
const normalizeCountryValue = (value: string) => value.replace(/\s+/g, '');

export function translateCountryOfOrigin(country: string | null | undefined, t: (key: string) => string): string {
    if (!country) return '';
    const trimmed = country.trim();

    if (!trimmed) {
        return '';
    }

    const normalized = normalizeCountryValue(trimmed);

    const translationKey = `catalog.countries.${normalized}`;
    const countryTranslation = t(translationKey);
    if (countryTranslation && countryTranslation !== translationKey) {
        return countryTranslation;
    }

    if (normalized === PRODUCT_DETAIL_REFERENCE_VALUE) {
        const referenceTranslation = t('product.productDetailReference');
        if (referenceTranslation && referenceTranslation !== 'product.productDetailReference') {
            return referenceTranslation;
        }
    }

    return trimmed;
}

const DEFAULT_FALLBACK_LANGUAGES = ['ko', 'en'];

function normalizeLanguage(language?: string): string {
    return (language || 'ko').toLowerCase();
}

export function getLocalizedNameFromI18n(
    entity: { name?: string; nameI18n?: Record<string, string> } | null | undefined,
    language?: string,
    fallbackLanguages: string[] = DEFAULT_FALLBACK_LANGUAGES,
): string {
    if (!entity) return '';
    const { name = '', nameI18n } = entity;

    if (!nameI18n || Object.keys(nameI18n).length === 0) {
        return name;
    }

    const normalizedLanguage = normalizeLanguage(language);
    const entries = Object.entries(nameI18n).map(([key, value]) => [key.toLowerCase(), value] as const);
    const findMatch = (target: string) => entries.find(([key]) => key === target)?.[1] ?? '';

    const directMatch = findMatch(normalizedLanguage);
    if (directMatch) return directMatch;

    if (normalizedLanguage.includes('-')) {
        const baseLanguage = normalizedLanguage.split('-')[0];
        const baseMatch = findMatch(baseLanguage);
        if (baseMatch) return baseMatch;
    }

    if (normalizedLanguage === 'zh') {
        const simplified = findMatch('zh-cn');
        if (simplified) return simplified;
        const traditional = findMatch('zh-tw');
        if (traditional) return traditional;
    }

    for (const fallback of fallbackLanguages) {
        const fallbackMatch = findMatch(normalizeLanguage(fallback));
        if (fallbackMatch) return fallbackMatch;
    }

    return name || entries[0]?.[1] || '';
}

export function getLocalizedGroupTitle(
    product: { groupTitle?: string; groupTitleI18n?: Record<string, string> } | null | undefined,
    language?: string,
): string {
    if (!product) return '';
    return getLocalizedNameFromI18n(
        {
            name: product.groupTitle ?? '',
            nameI18n: product.groupTitleI18n,
        },
        language,
    );
}

export function getLocalizedDescription(
    product: { description?: string; descriptionI18n?: Record<string, string> } | null | undefined,
    language?: string,
): string {
    if (!product) return '';
    return getLocalizedNameFromI18n(
        {
            name: product.description ?? '',
            nameI18n: product.descriptionI18n,
        },
        language,
    );
}

export function translateWashOptionLabel(
    category: string,
    value: string,
    t: (key: string) => string,
    fallback?: string,
): string {
    if (!category || !value) return fallback || '';

    const normalizedCategory = category.toLowerCase();
    const normalizedValue = value.toLowerCase();

    const key = `product.washOptions.${normalizedCategory}.${normalizedValue}`;
    const translated = t(key);
    if (translated && translated !== key) {
        return translated;
    }

    return fallback || '';
}

type StoreLocationTokens = {
    building: string | null;
    floor: string | null;
    column: string | null;
    unit: string | null;
    rest: string;
};

export function parseStoreLocation(location?: string | null): StoreLocationTokens {
    if (!location) {
        return { building: null, floor: null, column: null, unit: null, rest: '' };
    }

    const trimmed = location.trim();
    
    // 쉼표로 구분된 형식인지 확인
    if (trimmed.includes(',')) {
        const parts = trimmed
            .split(',')
            .map((part) => part.trim());
        // 빈 값도 포함하여 파싱 (예: "동대문,디오트,지하1층,,1")
        const [regionOrBuilding, buildingOrFloor, floorOrColumn, columnOrUnit, unitOrRest, ...rest] = parts;
        
        // 패턴 매칭으로 각 필드 식별
        let building: string | null = null;
        let floor: string | null = null;
        let column: string | null = null;
        let unit: string | null = null;
        const restParts: string[] = [];
        
        // 지역명 목록
        const regionNames = ['동대문', '남대문', '부산'];
        
        // 각 부분을 순서대로 확인
        for (const part of parts) {
            if (!part) continue; // 빈 값 스킵
            
            // 층 패턴: "지하1층", "4층", "1층" 등
            if (!floor && /^(지하)?\d+층$/.test(part)) {
                floor = part;
                continue;
            }
            
            // 열 패턴: "F열", "1열", "J열" 등 (숫자만 있는 경우도 열일 수 있음)
            if (!column && (/^[A-Z가-힣]+열$/.test(part) || (/^\d+$/.test(part) && !unit))) {
                // 숫자만 있는 경우, 다음에 호가 오는지 확인 필요하지만 일단 열로 처리
                if (/^[A-Z가-힣]+열$/.test(part)) {
                    column = part;
                    continue;
                } else if (/^\d+$/.test(part) && !column) {
                    // 숫자만 있는 경우, 층 다음이면 열일 가능성이 높음
                    column = part;
                    continue;
                }
            }
            
            // 호 패턴: "123호", "16호" 등
            if (!unit && /^\d+호$/.test(part)) {
                unit = part;
                continue;
            }
            
            // 빌딩명: 지역명이 아닌 첫 번째 부분
            if (!building && !regionNames.includes(part) && !floor && !column && !unit) {
                building = part;
                continue;
            }
            
            // 나머지
            if (part !== building && part !== floor && part !== column && part !== unit) {
                restParts.push(part);
            }
        }
        
        // 빌딩명이 없고 지역명만 있는 경우, 두 번째 부분을 빌딩명으로 사용
        if (!building && regionOrBuilding && regionNames.includes(regionOrBuilding)) {
            building = buildingOrFloor || null;
        }
        
        return {
            building: building || null,
            floor: floor || null,
            column: column || null,
            unit: unit || null,
            rest: restParts.join(' ').trim(),
        };
    }

    // 공백으로 구분된 형식 처리 (예: "디오트 4층 F열 16호 위크플랜 weekplan")
    const parts = trimmed.split(/\s+/).filter(Boolean);
    let building: string | null = null;
    let floor: string | null = null;
    let column: string | null = null;
    let unit: string | null = null;
    const restParts: string[] = [];

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        // 층 패턴: "4층", "지하1층", "1층" 등
        if (!floor && /^(지하)?\d+층$/.test(part)) {
            floor = part;
            continue;
        }
        
        // 열 패턴: "F열", "1열", "J열" 등
        if (!column && /^[A-Z0-9가-힣]+열$/.test(part)) {
            column = part;
            continue;
        }
        
        // 호 패턴: "16호", "123호" 등
        if (!unit && /^\d+호$/.test(part)) {
            unit = part;
            continue;
        }
        
        // 빌딩명은 처음에 나오는 것으로 간주 (층/열/호 패턴이 아닌 첫 번째 부분)
        if (!building && !floor && !column && !unit) {
            building = part;
            continue;
        }
        
        // 나머지는 rest에 추가
        restParts.push(part);
    }

    return {
        building: building || null,
        floor: floor || null,
        column: column || null,
        unit: unit || null,
        rest: restParts.join(' ').trim(),
    };
}
